// AiMenu — THE one dropdown/menu system for the two Symbols AI surfaces
// (the canvas prompt composer's ModelBtn/ParamsBtn dropdowns and the
// assistant drawer's Sessions / model / settings / mention panels), extracted
// 2026-08-12 per Nika "improve and redesign all dropdowns in
// CanvasPromptTextarea and AISidebar — make design exceptionally good".
//
// Three registered pieces, one language:
//
//   • AiMenuPanel   — the panel surface + motion. `canvas-card-glass` (the
//     house dropdown surface — shared/functions openDropdown defaults every
//     plugin panel to it, so inline panels MUST match), radiusCard,
//     borderless (the glass theme paints a 1px hairline; fill contrast is
//     the separation device — design-direction.md §8), padding Z, minWidth
//     G, 60vh cap with internal scroll. NO shadow prop: the glass theme
//     carries the single budget shadow itself (brand shadow budget = 1).
//     Motion is the brand `Dropdown` recipe — hidden base (opacity 0 /
//     visibility hidden / a Y lift) + a `.isOpen` conditional block tweened
//     by `A defaultBezier` (~150ms). CONSUMERS declare the `isOpen`
//     predicate + their own positioning; the recipe re-fires reliably since
//     smbls bf7d72268 (registerConditionalEffects). `visibility` keeps the
//     closed panel out of the tab order and rides transitionProperty so it
//     holds until the fade-out ends. prefers-reduced-motion is enforced
//     globally by the brand reset — never re-implemented here.
//
//   • AiMenuHeading — the section caption: Y2 / 700 / caption ink /
//     uppercase / .08em, inset to the option rows' Z1 text edge (the
//     AiSettingsPanel sectionHeader pattern, now THE one heading).
//
//   • AiOptionRow   — the option row: MenuPanelItem metrics (Y/Z1 padding,
//     radiusControl, borderless, 44pt coarse-pointer floor) + leading icon
//     slot + Label/Sub body + right-aligned meta + trailing check. Selected
//     = the house `selected` wash + `title` ink — monochrome, never a hue
//     fill. Disabled rows stay real click targets (dim wash + `title`
//     tooltip + blocked onClick) and Sub keeps its one-line slot so a
//     demoted row never grows and shifts its neighbours.
//
// Per-row state contract (childrenAs: 'state'):
//   { key, label, sub, active?, disabled?, icon?, meta? }
// `onClick` comes from the consumer (AiModelSwitcherRow persists the model
// mode; AiSettingsPanel's sections dispatch their own choose* helpers).
//
// NO `transition` on the reactive background anywhere in this file — the
// runtime re-applies reactive backgrounds per tick, perpetually restarting
// the transition, which makes hover appear dead (MenuPanel.js note +
// tickets/smbls.md).

export const AiMenuPanel = {
  flow: 'y',
  minWidth: 'G',
  maxWidth: '90vw',
  maxHeight: '60dvh',
  overflow: 'auto',
  // Don't chain the panel's edge-bounce to the surface behind it.
  overscrollBehavior: 'contain',
  padding: 'Z',
  round: 'radiusCard',
  theme: 'canvas-card-glass',
  // The glass theme paints a 1px border; menus separate by fill contrast
  // alone. Longhand borderWidth (not `border: 'none'`) — the theme emits
  // longhands, so only a longhand zero wins (CanvasPromptTextarea note).
  borderWidth: '0',
  // Menus are overlay-level chrome (MenuPanel, the house recipe, carries
  // the same token). Needed because the glass theme's OWN light-half
  // shadow (`black.01`) computes to `none` live (measured 2026-08-13 —
  // ticketed, brand is read-only), so a light-theme panel over an empty
  // white ground had NO separation at all. One box-shadow value renders
  // either way — the elevation budget stays 1.
  shadow: 'overlay',
  boxSizing: 'border-box',
  textAlign: 'left',

  // Hidden base — the consumer's `isOpen` predicate flips the block below.
  // The lift reads `var(--spacing-Y)` (~6.5px): `transform` is a raw
  // passthrough in css-in-props, so a bare token inside translate3d() would
  // reach the browser verbatim and be dropped as invalid.
  opacity: '0',
  visibility: 'hidden',
  transform: 'translate3d(0, calc(-1 * var(--spacing-Y)), 0)',
  transition: 'A defaultBezier',
  transitionProperty: 'opacity, transform, visibility',
  '.isOpen': {
    opacity: '1',
    visibility: 'visible',
    transform: 'translate3d(0, 0, 0)'
  },

  '@mobileL': {
    minWidth: '0',
    maxWidth: 'calc(100dvw - var(--spacing-B))'
  }
}

// The section caption is the kit's mono micro-label (DESIGN_SPEC §5 — every
// section head in the redesign), aligned to the option rows' text edge.
export const AiMenuHeading = {
  extends: 'AppMicroLabel',
  display: 'block',
  lineHeight: '1',
  // Align to the option rows' text edge. Spacing tokens are em-relative:
  // the rows inset Z1 at their Z1 font, so the caption's own inset is the
  // nearest on-scale step at ITS font (the exact px does not exist on the em
  // scale without a raw value).
  paddingLeft: 'Z2'
}

export const AiOptionRow = {
  tag: 'button',
  type: 'button',
  // Keyboard/AT contract (2026-09-02) — `menuitem` is the marker every menu
  // panel in this system walks with `menuArrowNav`
  // (shared/functions/menuKeyboard.js), the SAME marker ContextDropdown's own
  // rows carry. Deliberately NO `tabindex: -1` roving here: this exact row is
  // also mounted inside the AI SETTINGS panel, a large drawer/dropdown body
  // where Tab is the primary way through the sections — taking the rows out
  // of the tab order there would trade one keyboard gap for a worse one. The
  // arrow cycle is therefore ADDITIVE to native tabbing, and a native
  // <button> already activates on Enter AND Space with no extra handler.
  role: 'menuitem',
  ariaDisabled: (el, s) => (s?.disabled ? 'true' : null),
  width: '100%',
  flow: 'x',
  align: 'center',
  gap: 'A',
  // MenuPanelItem metrics, restated (not extended) so the disabled-aware
  // reactive `:hover` below is the row's ONLY hover rule — a base-class
  // static hover would race it on disabled rows.
  padding: 'Y Z1',
  round: 'radiusControl',
  fontSize: 'Z1',
  textAlign: 'left',
  borderWidth: '0',
  color: 'title',
  whiteSpace: 'normal',
  // 44pt touch floor on coarse pointers (Apple HIG) — same as MenuPanelItem.
  '@coarse': { minHeight: 'controlH' },
  // A disabled row carries no opacity (DESIGN_SPEC §8.1: an inert control
  // steps its INK to `inactive`, it never fades) — the label below does.
  cursor: (el, s) => (s.disabled ? 'not-allowed' : 'pointer'),
  // A disabled row's reason stays reachable via the native tooltip.
  title: (el, s) => (s.disabled ? s.sub : ''),
  background: (el, s) => (s.active ? 'selected' : 'transparent'),
  ':hover': { background: (el, s) => (s.disabled ? 'transparent' : 'hover') },

  // Leading icon — renders only when the row's state names a glyph.
  // (Key-based auto-extend: `Icon` extends the Icon built-in — FA806.)
  Icon: {
    if: (el, s) => !!s.icon,
    name: (el, s) => s.icon,
    boxSize: 'A',
    color: (el, s) => (s.disabled ? 'inactive' : 'title'),
    flexShrink: '0'
  },

  Body: {
    flow: 'y',
    gap: 'V',
    flex: '1',
    minWidth: '0',
    lineHeight: '1.3',
    // Every row feed in this house carries a polyglot KEY in `label`/`sub`
    // (AUTH_ROWS, VOICE_ROWS, the pin rows, the scope segments). `translate`
    // returns its input unchanged when a key does not resolve, so a model
    // name or any tenant string still passes through untouched. Resolving
    // HERE — at the one shared row — is what keeps every AI menu from
    // printing raw dotted keys on screen.
    // Labels are 500 (the weight law); an inert row's label steps to
    // `inactive`.
    Label: {
      tag: 'span',
      fontWeight: '500',
      color: (el, s) => (s.disabled ? 'inactive' : 'title'),
      text: (el, s) => el.call('polyglot', s.label || '')
    },
    Sub: {
      tag: 'span',
      fontSize: 'Y',
      color: 'caption',
      lineHeight: '1.4',
      text: (el, s) => el.call('polyglot', s.sub || '')
    }
  },

  // Right-aligned meta (kbd hint / sub-label) — renders only when provided.
  Meta: {
    if: (el, s) => !!s.meta,
    tag: 'span',
    fontSize: 'Y',
    color: 'caption',
    flexShrink: '0',
    text: (el, s) => el.call('polyglot', s.meta || '')
  },

  // Trailing checkmark — the active row's second cue beside the wash.
  // (`Icon_1` auto-extends the Icon built-in; the `_1` suffix is ignored
  // for extend resolution — FA806.)
  Icon_1: {
    name: 'checkmark',
    boxSize: 'Z',
    color: 'title',
    flexShrink: '0',
    hide: (el, s) => !s.active
  }
}
